import { Modal } from "@mui/material";
import { Box } from "@mui/system";
import React, { useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomButton from "../Buttons/Button";
import { downloadStockTemplate } from "../../store/actions/stocks/downloadStockTemplate";
import { bulkUploadStocks } from "../../store/actions/stocks/bulkUploadStocks";

const ACCEPTED_TYPES = [
  "text/csv",
  "application/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const ACCEPTED_EXTENSIONS = [".csv", ".xls", ".xlsx"];

function BulkUploadStockModal({ open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const downloadPending = useSelector(
    (state) => state?.stocks?.downloadTemplatePending
  );
  const uploadPending = useSelector(
    (state) => state?.stocks?.bulkUploadPending
  );

  const resetAndClose = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    const name = selected.name?.toLowerCase() || "";
    const isAccepted =
      ACCEPTED_TYPES.includes(selected.type) ||
      ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));

    if (!isAccepted) {
      toast.error("Please select a valid CSV or Excel file.");
      return;
    }
    setFile(selected);
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadStockTemplate({}));
  };

  const handleUpload = () => {
    if (!file) {
      toast.error("Please select a CSV file to upload.");
      return;
    }

    const data = new FormData();
    data.append("file", file);

    dispatch(
      bulkUploadStocks({
        data,
        callback: () => {
          onSuccess && onSuccess();
          resetAndClose();
        },
      })
    );
  };

  return (
    <Modal open={open} onClose={resetAndClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          maxWidth: "92%",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 2,
        }}
      >
        <IconButton
          aria-label="close"
          sx={{ position: "absolute", top: 19, right: 10 }}
          onClick={resetAndClose}
        >
          <ClearIcon />
        </IconButton>

        <div className="w-full" style={{ padding: "10px 20px" }}>
          <h1 className="font-semibold text-2xl mb-1">Bulk Upload Stocks</h1>
          <p className="text-sm text-[#667085] mb-5">
            Download the template, fill in the stock details, then upload the
            completed file to create multiple stocks at once.
          </p>

          <div className="mb-5">
            <p className="font-medium mb-1">Step 1 — Download template</p>
            <CustomButton
              variant="outlined"
              customColor="#fff"
              children="Download Template"
              width="100%"
              height="48px"
              loading={downloadPending}
              borderColor="#159AA8"
              color="#159AA8"
              onClick={handleDownloadTemplate}
            />
          </div>

          <div className="mb-6">
            <p className="font-medium mb-1">Step 2 — Upload completed file</p>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="cursor-pointer border border-dashed border-[#D0D5DD] rounded-[8px] px-4 py-6 text-center"
            >
              <img
                src="/Assets/uploadIcon.png"
                alt=""
                className="w-10 h-10 mx-auto mb-2"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <p className="text-sm text-[#159AA8] font-medium">
                {file ? "Change file" : "Click to select a CSV or Excel file"}
              </p>
              {file ? (
                <p className="text-xs text-[#667085] mt-1 break-all">
                  {file.name}
                </p>
              ) : (
                <p className="text-xs text-[#667085] mt-1">
                  CSV or Excel up to 5MB
                </p>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>

          <div className="flex w-full items-center">
            <CustomButton
              variant="outlined"
              customColor="#fff"
              children="Cancel"
              width="100%"
              onClick={resetAndClose}
              height="50px"
              borderColor="#D0D5DD"
              margin="0px 25px 0px 0px"
              color="#344054"
            />
            <CustomButton
              variant="contained"
              customColor="#159AA8"
              loading={uploadPending}
              children="Upload Stocks"
              width="100%"
              height="50px"
              padding="10px"
              color="#fff"
              onClick={handleUpload}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default BulkUploadStockModal;
